import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import StaggerReveal from '../../../components/feature/StaggerReveal';
import { useTilt } from '../../../hooks/useTilt';

const WHATSAPP_URL = 'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20interesa%20conocer%20sus%20servicios%20digitales.';
const BOOK_URL = '#book-demo';

interface PillarData {
  icon: string;
  titleKey: string;
  subKey: string;
  accentColor: string;
  featureKeys: string[];
  forLabelKey: string;
  tagKeys: string[];
  numberLabel: string;
}

const DIGITAL_PILLARS: PillarData[] = [
  {
    icon: 'ri-smartphone-line',
    titleKey: 'digital_p1_title',
    subKey: 'digital_p1_sub',
    accentColor: '#00cfff',
    featureKeys: ['digital_p1_f1', 'digital_p1_f2', 'digital_p1_f3', 'digital_p1_f4'],
    forLabelKey: 'digital_p1_for',
    tagKeys: ['digital_p1_tag1', 'digital_p1_tag2', 'digital_p1_tag3'],
    numberLabel: '01',
  },
  {
    icon: 'ri-global-line',
    titleKey: 'digital_p2_title',
    subKey: 'digital_p2_sub',
    accentColor: '#facc15',
    featureKeys: ['digital_p2_f1', 'digital_p2_f2', 'digital_p2_f3', 'digital_p2_f4'],
    forLabelKey: 'digital_p2_for',
    tagKeys: ['digital_p2_tag1', 'digital_p2_tag2', 'digital_p2_tag3'],
    numberLabel: '02',
  },
  {
    icon: 'ri-code-s-slash-line',
    titleKey: 'digital_p3_title',
    subKey: 'digital_p3_sub',
    accentColor: '#f97316',
    featureKeys: ['digital_p3_f1', 'digital_p3_f2', 'digital_p3_f3', 'digital_p3_f4'],
    forLabelKey: 'digital_p3_for',
    tagKeys: ['digital_p3_tag1', 'digital_p3_tag2', 'digital_p3_tag3'],
    numberLabel: '03',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    titleKey: 'digital_p4_title',
    subKey: 'digital_p4_sub',
    accentColor: '#00ff9d',
    featureKeys: ['digital_p4_f1', 'digital_p4_f2', 'digital_p4_f3', 'digital_p4_f4'],
    forLabelKey: 'digital_p4_for',
    tagKeys: ['digital_p4_tag1', 'digital_p4_tag2', 'digital_p4_tag3'],
    numberLabel: '04',
  },
];

interface PillarCardProps {
  pillar: PillarData;
  isLight: boolean;
}

function PillarCard({ pillar, isLight }: PillarCardProps) {
  const { t } = useTranslation();
  const { cardRef, shineRef, isHovered, handlers } = useTilt({
    maxTilt: 11,
    scale: 1.022,
    followSpeed: 0.05,
    resetSpeed: 0.65,
  });

  const textMain = isLight ? '#0a1628' : '#ffffff';
  const textSub  = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';
  const cardBg   = isLight ? '#ffffff' : 'rgba(10,20,40,0.55)';

  return (
    <div
      ref={cardRef}
      className="relative rounded-2xl overflow-hidden flex flex-col cursor-default will-change-transform"
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${isHovered ? pillar.accentColor + '55' : isLight ? pillar.accentColor + '28' : pillar.accentColor + '20'}`,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        boxShadow: isHovered
          ? isLight
            ? `0 24px 50px rgba(0,0,0,0.10), 0 0 35px ${pillar.accentColor}22`
            : `0 24px 60px rgba(0,0,0,0.50), 0 0 35px ${pillar.accentColor}22`
          : isLight ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
      }}
      {...handlers}
    >
      <div
        ref={shineRef}
        className="absolute inset-0 z-10 pointer-events-none rounded-2xl"
        style={{ opacity: 0, transition: 'opacity 0.35s ease' }}
      />

      {/* Top gradient accent bar */}
      <div
        className="h-1 w-full flex-shrink-0"
        style={{ background: `linear-gradient(to right, ${pillar.accentColor}, ${pillar.accentColor}40)` }}
      />

      <div className="p-5 sm:p-6 md:p-7 flex flex-col flex-1 gap-4 md:gap-5 relative z-[1]">
        {/* Icon + Title */}
        <div className="flex items-start gap-3 md:gap-4">
          <div
            className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl flex-shrink-0"
            style={{
              backgroundColor: isHovered ? `${pillar.accentColor}28` : `${pillar.accentColor}18`,
              color: pillar.accentColor,
              transform: isHovered ? 'scale(1.1) rotate(6deg)' : 'scale(1) rotate(0deg)',
              transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1), background-color 0.3s ease',
            }}
          >
            <i className={`${pillar.icon} text-xl md:text-2xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3
                className="font-heading text-base md:text-lg font-bold leading-snug"
                style={{ color: textMain }}
              >
                {t(pillar.titleKey)}
              </h3>
              <span
                className="font-heading text-xs font-bold flex-shrink-0"
                style={{ color: `${pillar.accentColor}60` }}
              >
                {pillar.numberLabel}
              </span>
            </div>
            <p className="font-body text-xs leading-relaxed" style={{ color: textSub }}>
              {t(pillar.subKey)}
            </p>
          </div>
        </div>

        {/* Features */}
        <ul className="flex flex-col gap-2 md:gap-2.5 flex-1">
          {pillar.featureKeys.map((key, i) => (
            <li
              key={key}
              className="flex items-start gap-2 md:gap-2.5"
              style={{
                transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                transition: `transform 0.3s ease ${i * 50}ms`,
              }}
            >
              <div
                className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                style={{ backgroundColor: `${pillar.accentColor}20`, color: pillar.accentColor }}
              >
                <i className="ri-check-line text-xs" />
              </div>
              <span className="font-body text-xs sm:text-sm leading-relaxed" style={{ color: textSub }}>
                {t(key)}
              </span>
            </li>
          ))}
        </ul>

        {/* Divider + Tags */}
        <div
          className="flex items-center gap-2 flex-wrap pt-3 md:pt-4 border-t"
          style={{ borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.07)' }}
        >
          <span className="font-body text-xs" style={{ color: textSub }}>
            {t(pillar.forLabelKey)}
          </span>
          {pillar.tagKeys.map((tagKey) => (
            <span
              key={tagKey}
              className="px-2 py-0.5 rounded-full text-xs font-semibold font-body transition-colors duration-300"
              style={{
                backgroundColor: isHovered ? `${pillar.accentColor}22` : `${pillar.accentColor}12`,
                color: pillar.accentColor,
              }}
            >
              {t(tagKey)}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom glow bar on hover */}
      <div
        className="absolute bottom-0 left-0 h-[2px] z-[1]"
        style={{
          background: `linear-gradient(to right, ${pillar.accentColor}, ${pillar.accentColor}00)`,
          width: isHovered ? '100%' : '0%',
          transition: 'width 0.45s ease',
        }}
      />
    </div>
  );
}

export default function DigitalServices() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useTranslation();

  const sectionBg = isLight ? '#ffffff' : '#060f23';
  const textMain  = isLight ? '#0a1628' : '#ffffff';
  const textSub   = isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)';

  return (
    <section
      id="digital-services"
      className="py-16 md:py-28 px-4 sm:px-5 md:px-6"
      style={{ background: sectionBg }}
    >
      <div className="max-w-7xl mx-auto">

        {/* ── Section divider label ─────────────────────────────── */}
        <div className="flex items-center gap-4 mb-10 md:mb-14">
          <div
            className="flex-1 h-px"
            style={{ background: isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.08)' }}
          />
          <div
            className="flex items-center gap-2 px-4 py-1.5 rounded-full border"
            style={{
              borderColor: isLight ? 'rgba(249,115,22,0.35)' : 'rgba(249,115,22,0.30)',
              backgroundColor: isLight ? 'rgba(249,115,22,0.06)' : 'rgba(249,115,22,0.08)',
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#f97316' }}>
              <i className="ri-code-s-slash-line text-sm" />
            </div>
            <span
              className="font-body text-xs font-semibold tracking-widest uppercase"
              style={{ color: '#f97316' }}
            >
              {t('digital_label')}
            </span>
          </div>
          <div
            className="flex-1 h-px"
            style={{ background: isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.08)' }}
          />
        </div>

        {/* Header */}
        <div className="text-center mb-10 md:mb-14 px-2 sm:px-0">
          <h2
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight"
            style={{ color: textMain }}
          >
            {t('digital_title_1')}{' '}
            <span style={{ color: '#f97316' }}>{t('digital_title_accent')}</span>
          </h2>
          <p
            className="font-body text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
            style={{ color: textSub }}
          >
            {t('digital_sub')}
          </p>
        </div>

        {/* 4 Pillar Cards — 2x2 grid */}
        <StaggerReveal
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-8 md:mb-10"
          staggerDelay={120}
        >
          {DIGITAL_PILLARS.map((pillar) => (
            <PillarCard key={pillar.titleKey} pillar={pillar} isLight={isLight} />
          ))}
        </StaggerReveal>

        {/* Bottom CTA strip */}
        <div
          className="relative rounded-2xl overflow-hidden border"
          style={{
            background: isLight
              ? 'linear-gradient(135deg, rgba(249,115,22,0.06) 0%, rgba(250,204,21,0.04) 100%)'
              : 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(250,204,21,0.05) 100%)',
            borderColor: isLight ? 'rgba(249,115,22,0.22)' : 'rgba(249,115,22,0.18)',
          }}
        >
          {/* BG glows */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%)' }}
            />
            <div
              className="absolute -bottom-10 left-12 w-48 h-48 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.10) 0%, transparent 70%)' }}
            />
          </div>

          <div className="relative z-10 p-5 sm:p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-5 md:gap-6">
            <div className="w-full md:max-w-xl text-center md:text-left">
              <h3
                className="font-heading text-lg md:text-2xl font-bold mb-2"
                style={{ color: textMain }}
              >
                {t('digital_cta_heading')}
              </h3>
              <p className="font-body text-sm leading-relaxed" style={{ color: textSub }}>
                {t('digital_cta_desc')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto flex-shrink-0">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1db954] text-white font-heading font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 w-full sm:w-auto"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-base" />
                </div>
                {t('digital_cta_ask')}
              </a>
              <a
                href={BOOK_URL}
                className="flex items-center justify-center gap-2.5 font-heading font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 w-full sm:w-auto"
                style={{ backgroundColor: '#f97316', color: '#ffffff' }}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-base" />
                </div>
                {t('digital_cta_book')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
